"use client";

import Button from "@/components/ui/button/Button";
import SearchableSelect from "@/components/ui/SearchableSelect";
import Radio from "@/components/form/input/Radio";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { toIsoDateOnly } from "@/lib/dateApi";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

function sameRateMap(a, b) {
  const aKeys = Object.keys(a || {});
  const bKeys = Object.keys(b || {});
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => String(a[k]) === String(b[k]));
}

export default function QuotationForm({ onDone, quotation = null }) {
  const qc = useQueryClient();
  const [date, setDate] = React.useState(new Date().toISOString().slice(0, 10));
  const [validUntil, setValidUntil] = React.useState("");
  const [partyId, setPartyId] = React.useState(null);
  const [taxType, setTaxType] = React.useState("NON_GST");
  const [gstRate, setGstRate] = React.useState("");
  const [rates, setRates] = React.useState({});
  const [pendingRates, setPendingRates] = React.useState(null);
  const isEdit = quotation?.id != null;

  const { data: parties = [] } = useQuery({
    queryKey: ["salesParties"],
    queryFn: async () => (await rmcClient.listSalesParties()).data,
  });
  const { data: grades = [], isFetched: areGradesFetched } = useQuery({
    queryKey: ["grades", partyId],
    queryFn: async () => (await rmcClient.listGrades({ party_id: partyId })).data,
    enabled: partyId != null,
  });
  const { data: gstMasters = [] } = useQuery({
    queryKey: ["gstMasters", true],
    queryFn: async () => (await rmcClient.listGstMasters({ is_active: true })).data,
  });

  React.useEffect(() => {
    if (!quotation) {
      setPendingRates(null);
      setTaxType("NON_GST");
      setGstRate("");
      return;
    }
    setDate(toIsoDateOnly(quotation.date) || "");
    setValidUntil(toIsoDateOnly(quotation.valid_until) || "");
    setPartyId(quotation.party_id ?? quotation.party?.id ?? null);
    setTaxType(quotation.tax_type === "GST" ? "GST" : "NON_GST");
    setGstRate(quotation.gst_rate != null ? String(quotation.gst_rate) : "");
    const mapped = {};
    if (Array.isArray(quotation.items)) {
      quotation.items.forEach((it) => {
        const gid = it.grade?.id;
        if (gid != null) mapped[gid] = String(it.rate ?? "");
      });
    } else if (Array.isArray(quotation.grade_rates)) {
      quotation.grade_rates.forEach((it) => {
        if (it?.grade_id != null) mapped[it.grade_id] = String(it.rate ?? "");
      });
    }
    setPendingRates(mapped);
  }, [quotation]);

  React.useEffect(() => {
    if (pendingRates == null) return;
    if (partyId != null && !areGradesFetched) return;
    const allowed = new Set(grades.map((g) => String(g.id)));
    const filtered = Object.fromEntries(
      Object.entries(pendingRates).filter(([gid]) => allowed.has(String(gid)))
    );
    setRates(filtered);
    setPendingRates(null);
  }, [grades, pendingRates, partyId, areGradesFetched]);

  React.useEffect(() => {
    if (pendingRates != null) return;
    setRates((prev) => {
      const allowed = new Set(grades.map((g) => String(g.id)));
      const next = Object.fromEntries(
        Object.entries(prev).filter(([gid]) => allowed.has(String(gid)))
      );
      return sameRateMap(prev, next) ? prev : next;
    });
  }, [partyId, grades, pendingRates]);

  const save = useMutation({
    mutationFn: () => {
      const dateNorm = toIsoDateOnly(date);
      const validNorm = toIsoDateOnly(validUntil);
      if (taxType === "GST" && !gstRate) throw new Error("gst");
      const grade_rates = grades
        .map((g) => ({
          grade_id: g.id,
          rate: rates[g.id] ? Number(rates[g.id]) : 0,
        }))
        .filter((x) => x.rate > 0);
      const body = {
        party_id: partyId,
        date: dateNorm,
        tax_type: taxType,
        grade_rates,
        is_active: quotation?.is_active ?? true,
      };
      if (taxType === "GST") body.gst_rate = gstRate;
      if (validNorm) body.valid_until = validNorm;
      if (isEdit) return rmcClient.updateQuotation(quotation.id, body);
      return rmcClient.createQuotation(body);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["quotations"] });
      if (isEdit) qc.invalidateQueries({ queryKey: ["quotation", quotation.id] });
      const qn = res?.data?.quotation_no ?? res?.quotation_no ?? "Quotation";
      toast.success(isEdit ? `${qn} updated` : `${qn} created`);
      setRates({});
      onDone?.();
    },
    onError: (e) => {
      if (e.message === "gst") toast.error("Select GST rate for GST quotation");
      else toast.error(isEdit ? "Failed to update" : "Failed to save");
    },
  });

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <SearchableSelect
            label="Party"
            value={partyId}
            onChange={setPartyId}
            options={parties}
            getSubLabel={(p) => p.gst_number}
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label>Valid Until</Label>
          <Input
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="sm:col-span-2">
          <Label>Tax Type</Label>
          <div className="mt-2 flex items-center gap-6">
            <Radio
              id="quotation-tax-gst"
              name="quotation-tax-type"
              value="GST"
              label="GST"
              checked={taxType === "GST"}
              onChange={() => setTaxType("GST")}
            />
            <Radio
              id="quotation-tax-non-gst"
              name="quotation-tax-type"
              value="NON_GST"
              label="Non-GST"
              checked={taxType === "NON_GST"}
              onChange={() => setTaxType("NON_GST")}
            />
          </div>
        </div>
        {taxType === "GST" && (
          <div className="sm:col-span-2">
            <SearchableSelect
              label="GST Rate"
              value={gstRate}
              onChange={(v) => setGstRate(v != null ? String(v) : "")}
              options={gstMasters}
              getLabel={(g) =>
                `${Number(g.rate || 0).toFixed(2)}%${g.remark ? ` - ${g.remark}` : ""}`
              }
              getValue={(g) => String(g.rate)}
              placeholder="Select GST rate…"
              overlayZClass="z-[100001]"
              menuZClass="z-[100002]"
            />
          </div>
        )}
      </div>
      <div className="rounded-lg border border-gray-800 overflow-hidden">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="border-b border-gray-800 bg-gray-950/80">
              <th className="px-3 py-2 text-left text-gray-500">Grade</th>
              <th className="px-3 py-2 text-left text-gray-500">Rate (₹ per CUM)</th>
            </tr>
          </thead>
          <tbody>
            {grades.map((g) => (
              <tr key={g.id} className="border-b border-gray-800/80">
                <td className="px-3 py-2 text-white">{g.name}</td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="—"
                    value={rates[g.id] ?? ""}
                    onChange={(e) =>
                      setRates({ ...rates, [g.id]: e.target.value })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button
        size="sm"
        disabled={!partyId || save.isPending}
        onClick={() => {
          const dateNorm = toIsoDateOnly(date);
          if (!dateNorm) {
            toast.error("Please set a valid date (YYYY-MM-DD).");
            return;
          }
          save.mutate();
        }}
      >
        {save.isPending ? (isEdit ? "Updating…" : "Saving…") : isEdit ? "Update Quotation" : "Save Quotation"}
      </Button>
    </div>
  );
}
